<template>
  <div class="info">
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-bind:role="'button'" v-bind:svg-inline="''" v-bind:class="'info-bubble'" @click.stop="toggle" @mouseenter="onhover && open()" @mouseleave="onhover && close()" v-bind:tabindex="'0'" @keydown.enter.stop="toggle" v-bind:focusable="'false'"><path d="M15.07 11.25l-.9.92C13.45 12.89 13 13.5 13 15h-2v-.5c0-1.11.45-2.11 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41a2 2 0 00-2-2 2 2 0 00-2 2H8a4 4 0 014-4 4 4 0 014 4 3.2 3.2 0 01-.93 2.25M13 19h-2v-2h2M12 2A10 10 0 002 12a10 10 0 0010 10 10 10 0 0010-10c0-5.53-4.5-10-10-10z"/></svg>
    <transition name="dialog" @enter="enter">
      <div class="info-dialog"
           ref="dialogBox"
           tabindex="-1"
           :class="sideClass"
           :style="styleDialog"
           v-if="isOpen"
      >
        <!-- Corner could have been a pseudo element, but is not able to
                provide the flexibility needed in combination with computed props -->
        <div class="info-dialog-corner" :style="styleCorner"></div>
        <div class="info-dialog-title">{{ title }}</div>
        <slot />
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
// This component calculates the arrow position by itself, in case headache-causing
// issues arise, or more flexibility is needed, an external library like popper.js might be used

import { defineComponent } from "vue";

const dialogWindowMargin = 10;
const dialogBubbleMargin = 40;
const dialogMaxWidth = 370 + dialogBubbleMargin;
const dialogCornerSize = 12;
const dialogCornerBaseOffset = 14;

export default defineComponent({
  props: {
    title: {
      type: String,
    },
    onhover: {
      type: Boolean,
      default: false,
    },
    side: {
      type: String,
      default: "auto",
      validator(val: string) {
        return ["auto", "right", "bottom", "left", "top"].includes(val);
      },
    },
  },
  data() {
    return {
      isOpen: false,
      offsetX: 0,
      offsetY: 0,
      recommendedSide: "right",
    };
  },
  computed: {
    sideClass(): string {
      return "is" + this.autoSide[0].toUpperCase() + this.autoSide.substr(1);
    },
    autoSide(): string {
      return this.side === "auto" ? this.recommendedSide : this.side;
    },

    // styleDialog and styleCorner may be abstracted more by calculating
    // the position and size of its parent, getting rid of all fixed numbers
    styleDialog(): object {
      if (this.autoSide === "right")
        return {
          top: -(this.offsetY + dialogWindowMargin) + "px",
          left: dialogBubbleMargin + "px",
          transformOrigin: `left ${dialogCornerBaseOffset + this.offsetY}px`,
        };
      else if (this.autoSide === "bottom")
        return {
          top: dialogBubbleMargin + "px",
          left: -(this.offsetX + dialogWindowMargin) + "px",
          transformOrigin: `${dialogCornerBaseOffset + this.offsetX}px top`,
        };
      else if (this.autoSide === "top")
        return {
          bottom: dialogBubbleMargin + "px",
          left: -(this.offsetX + dialogWindowMargin) + "px",
          transformOrigin: `${dialogCornerBaseOffset + this.offsetX}px bottom`,
        };
      else if (this.autoSide === "left")
        return {
          top: -(this.offsetY + dialogWindowMargin) + "px",
          right: dialogBubbleMargin + "px",
          transformOrigin: `right ${dialogCornerBaseOffset + this.offsetY}px`,
        };
      else
        console.error(
          "Info Dialog with unexpected side in styleDialog:",
          this.autoSide
        );
      return {};
    },
    styleCorner(): object {
      if (this.autoSide === "right")
        return {
          left: "-7px",
          top: `${dialogCornerBaseOffset + this.offsetY}px`,
          transform: "rotateZ(45deg)",
        };
      else if (this.autoSide === "bottom")
        return {
          top: "-7px",
          left: `${dialogCornerBaseOffset + this.offsetX}px`,
          transform: "rotateZ(135deg)",
        };
      else if (this.autoSide === "top")
        return {
          bottom: "-7px",
          left: `${dialogCornerBaseOffset + this.offsetX}px`,
          transform: "rotateZ(315deg)",
        };
      else if (this.autoSide === "left")
        return {
          right: "-7px",
          top: `${dialogCornerBaseOffset + this.offsetY}px`,
          transform: "rotateZ(225deg)",
        };
      else
        console.error(
          "Info Dialog with unexpected side in styleCorner:",
          this.autoSide
        );
      return {};
    },
  },
  methods: {
    toggle() {
      const questionIcon = this.$el.querySelector('.info-bubble') as HTMLElement | null;

      if (this.isOpen) {
        // Tooltip is open → we are closing it
        this.close();

        // Restore focus to the icon so keyboard keeps working
        if (questionIcon) {
          questionIcon.focus();
        }
      } else {
        // Tooltip is closed → we are opening it
        this.open();

        this.$nextTick(() => {
          const dialogBox = this.$refs.dialogBox as HTMLElement | undefined;
          if (dialogBox) {
            dialogBox.focus();

            // Optional: handle Enter press directly on tooltip to close
            dialogBox.addEventListener('keydown', (e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                this.close();
                if (questionIcon) questionIcon.focus();
              }
            }, { once: true });
          }
        });

        window.addEventListener("click", this.close, { once: true });
      }
    },

    close() {
      this.offsetX = 0;
      this.offsetY = 0;
      this.isOpen = false;
    },
    open() {
      this.recommendedSide = this.calcRecommendedSide();
      this.isOpen = true;
    },
    calcRecommendedSide(): string {
      const windowWidth = window.screen
        ? window.screen.width
        : window.innerWidth;
      const windowHeight = window.screen
        ? window.screen.height
        : window.innerHeight;
      const clientRect = this.$el.getBoundingClientRect();

      const clientRectSpace = {
        right: windowWidth - clientRect.right,
        top: clientRect.top,
        bottom: windowHeight - clientRect.bottom,
      };

      if (clientRectSpace.right > dialogMaxWidth) return "right";
      else if (clientRectSpace.bottom > clientRectSpace.top) return "bottom";
      else return "top";
    },
    enter(el: HTMLElement) {
      const windowWidth = window.screen
        ? window.screen.width
        : window.innerWidth;
      const windowHeight = window.screen
        ? window.screen.height
        : window.innerHeight;
      const clientRect = this.$el.getBoundingClientRect();

      const dialogWidth = el.offsetWidth;
      const dialogHeight = el.offsetHeight;
      const dialogLeft = clientRect.left + 27;
      const dialogTop = clientRect.top;
      const dialogRight = dialogLeft + dialogWidth;
      const dialogBottom = dialogTop + dialogHeight;

      const offsetX =
        windowWidth < dialogRight
          ? Math.min(
            dialogRight - windowWidth,
            dialogWidth - 2 * dialogCornerBaseOffset - dialogCornerSize
          )
          : 0;
      const offsetY =
        windowHeight < dialogBottom
          ? Math.min(
            dialogBottom - windowHeight,
            dialogHeight - 2 * dialogCornerBaseOffset - dialogCornerSize
          )
          : 0;
      this.offsetX = offsetX;
      this.offsetY = offsetY;
    },
  },
});
</script>

<style lang="scss" scoped>
$dialog-anim-spacing: 20px;
$dialog-anim-scaling: 0.9;
$dialog-margin: 10px;
$dialog-corner-size: 12px;

.info {
  position: relative;

  &-bubble {
    fill: $color-primary-blue;
    outline: none;
    cursor: pointer;
    transition: transform 0.2s ease;
    display: block;

    &:hover {
      transform: scale(1.5);
    }
    &:focus {
      border: 2px solid #9B89D0;
    }
  }

  &-dialog {
    position: absolute;
    z-index: 1;
    width: calc(100vw - #{2 * $dialog-margin});
    padding: 14px;
    background-color: $color-field-backdrop;
    border: 1px solid $color-primary-blue;
    max-width: 300px;
    display: block;

    &-corner {
      position: absolute;
      background-color: $color-field-backdrop;
      border-left: 1px solid $color-primary-blue;
      border-bottom: 1px solid $color-primary-blue;
      transform-origin: center center;
      width: $dialog-corner-size;
      height: $dialog-corner-size;
    }

    &-title {
      font-weight: bold;
    }
  }
}

.dialog-enter-active,
.dialog-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
  pointer-events: none;
}

.dialog-enter,
.dialog-leave-to {
  opacity: 0;
  transform: scale($dialog-anim-scaling);
  &.isRight {
    transform: translateX(-$dialog-anim-spacing) scale($dialog-anim-scaling);
  }
  &.isBottom {
    transform: translateY(-$dialog-anim-spacing) scale($dialog-anim-scaling);
  }
  &.isTop {
    transform: translateY($dialog-anim-spacing) scale($dialog-anim-scaling);
  }
  &.isLeft {
    transform: translateX($dialog-anim-spacing) scale($dialog-anim-scaling);
  }
}
</style>
